/**
 * Event and Stream Types
 * Types for events, live streams, and upcoming streams
 */

// ============================================================================
// Event Status
// ============================================================================

/**
 * Event status code to name mapping
 * Used across the application for displaying event states
 */
export const EVENT_STATUS_NAMES: Record<number, string> = {
  1: 'new',
  2: 'live',
  3: 'on break',
  4: 'available',
  5: 'editing',
  6: 'publishing',
  7: 'published',
  8: 'starting',
  9: 'disconnected',
  10: 'unpublishing',
  12: 'stopping',
};

/**
 * Get event status name from numeric code
 */
export function getEventStatusName(statusCode: number): string {
  return EVENT_STATUS_NAMES[statusCode] ?? 'unknown';
}

// ============================================================================
// Event Details
// ============================================================================

export interface EventDetailsResponse {
  eventID: string;
  title: string | null;
  description: string | null;
  thumbNum: number | null;
  autoStart: number;
  autoStop: number;
  autoPublish: number;
  locationID: number | null;
  eventNotes: string | null;
  originalFileName: string | null;
  customID: string | null;
  thumbURL: string | null;
}

/**
 * Full event details from GET /api/events/:clientID/:eventID
 * Includes infrastructure data when stream is active
 */
export interface EventFullDetailsResponse {
  // Core identifiers
  clientID: string;
  clientName: string | null;
  eventID: string;
  eventTitle: string | null;
  status: string;

  // Schedule
  eventStartDateTime: string | null;
  eventEndDateTime: string | null;
  autoStart: boolean;
  autoStop: boolean;
  preroll: number;

  // Metadata
  customID: string | null;
  description: string | null;
  notes: string | null;
  private: boolean;
  liveToBreak: boolean;

  // Assignment
  encoderID: string | null;

  // Infrastructure (from liveStreams when active)
  liveID: string | null;
  ingInstanceID: string | null;
  egInstanceID: string | null;
  wcStreamID: string | null;
  startDateTime: string | null;
  streamStatus: string | null;

  // Caption pipeline (populated when event had live AI captions)
  captionPipeline: CaptionPipelineData | null;

  // Simulcast destinations (populated when event has alt stream targets)
  simulcastTargets: SimulcastTarget[] | null;
}

// ============================================================================
// Simulcast Destinations (for event detail view)
// ============================================================================

export interface SimulcastTarget {
  destName: string | null;
  providerName: string;
  protocol: string;
  fqdn: string;
  transmitting: boolean;
  transmissionErrorMsg: string | null;
}

// ============================================================================
// Caption Pipeline (for event detail view)
// ============================================================================

export interface CaptionPipelineData {
  /** liveEventAutoCaption status (e.g., 'ended', 'running') */
  leacStatus: string | null;
  /** When the live caption session started */
  sessionStart: string | null;
  /** Duration of live caption session in seconds */
  captionDuration: number | null;
  /** When the archive (published) video asset was created */
  publishedAt: string | null;
  /** Caption VTT details */
  captionVtt: {
    exists: boolean;
    fileSize: number | null;
    createdAt: string | null;
    /** URL path for VTT analyzer */
    path: string | null;
  };
  /** Seconds between publish and caption creation */
  timeToCaptionSeconds: number | null;
}

// ============================================================================
// Event Status Diagnostic (pre-flight checks for manual status update)
// ============================================================================

export interface EventDiagnosticCheck {
  label: string;
  status: 'pass' | 'fail' | 'warn' | 'not_found';
  value: string | null;
  expected?: string;
}

export interface EventDiagnosticResponse {
  eventID: string;
  clientID: string;
  checks: EventDiagnosticCheck[];
  /** Most recent liveStream for this event */
  liveStream: {
    liveID: string;
    status: string | null;
    startDateTime: string | null;
    endDateTime: string | null;
    encoderID: string | null;
  } | null;
  /** Most recent transcode job for this event */
  transcodeJob: {
    id: string;
    status: string | null;
    progress: number | null;
    jobType: string | null;
    createdAt: string | null;
  } | null;
  /** Media asset with liveID (live recording) */
  mediaAsset: {
    assetID: string;
    currentStatus: string | null;
    assetType: string;
    fileSize: string | null;
    path: string | null;
    liveID: string | null;
  } | null;
  /** Current eventStatus record */
  eventStatus: {
    eventStatus: number | null;
    eventStatusName: string;
    published: number;
    lastError: string | null;
  } | null;
}

// ============================================================================
// Live Streams
// ============================================================================

export interface LiveStreamResponse {
  liveID: string;
  clientID: string | null;
  eventID: string | null;
  encoderID: string | null;
  ingInstanceID: string | null;
  egInstanceID: string | null;
  wcStreamID: string | null;
  startDateTime: string | null;
  endDateTime: string | null;
  status: string | null;
}

// Live stream with event and client details (for the /api/live-streams endpoint)
export interface LiveStreamWithEventResponse {
  // Core identifiers
  liveID: string | null; // null for "starting" events that don't have a liveStreams record yet
  clientID: string | null;
  clientName: string | null;
  clientTimeZone: string | null;  // Client's configured timezone (e.g., "America/New_York")
  eventID: string | null;
  eventTitle: string | null;
  status: string | null;        // Event status (what viewers see)
  streamStatus: string | null;  // Infrastructure status (is stream technically running)

  // Schedule
  eventStartDateTime: string | null;
  eventEndDateTime: string | null;
  startDateTime: string | null;
  endDateTime: string | null;
  autoStart: boolean;
  autoStop: boolean;
  preroll: number;

  // Encoder & Infrastructure
  encoderID: string | null;
  ingInstanceID: string | null;
  egInstanceID: string | null;
  wcStreamID: string | null;

  // Metadata
  customID: string | null;
  description: string | null;
  notes: string | null;

  // Visibility flags
  private: boolean;
  liveToBreak: boolean;
}

// Normalized row type for live streams table (used by both API and WebSocket data)
export interface LiveStreamTableRow {
  // Core identifiers
  clientID: string | null;
  clientName: string | null;
  clientTimeZone: string | null;  // Client's configured timezone (e.g., "America/New_York")
  eventID: string | null;
  eventTitle: string | null;
  liveID: string | null;
  status: string | null;
  streamStatus: string | null;  // Infrastructure status (from API)

  // Schedule
  eventStartDateTime: string | null;
  eventEndDateTime: string | null;
  startDateTime: string | null;
  endDateTime: string | null;
  autoStart: boolean;
  autoStop: boolean;
  preroll: number;

  // Encoder & Infrastructure
  encoderID: string | null;
  ingInstanceID: string | null;
  egInstanceID: string | null;
  wcStreamID: string | null;

  // URIs
  streamingURIs: {
    main: string | null;
    backup: string | null;
  } | null;

  // Metadata
  customID: string | null;
  description: string | null;
  notes: string | null;
  private: boolean;
  liveToBreak: boolean;

  // Live stats (from WebSocket)
  liveStats: {
    runtime: string;
    status: string;
  } | null;
}

export interface GetLiveStreamsResponse {
  liveStreams: LiveStreamWithEventResponse[];
}

// ============================================================================
// Upcoming Streams
// ============================================================================

export interface UpcomingStreamResponse {
  eventID: string;
  clientID: string;
  clientName: string;
  clientTimeZone: string | null;  // Client's configured timezone (e.g., "America/New_York")
  eventTitle: string | null;
  startDateTimePT: string; // ISO string converted to Pacific Time
  status: string; // Event status name (new, live, available, etc.) or 'upcoming' for future events
  encoderID: string | null;
}

export interface CacheInfo {
  cachedAt: string | null; // ISO string of when data was cached
  expiresAt: string | null; // ISO string of when cache expires
}

export interface GetUpcomingStreamsResponse {
  upcomingStreams: UpcomingStreamResponse[];
  cacheInfo: CacheInfo;
}
